/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {Badge} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {AnalyticsIds} from '@ion/core/src/analytics/types';
import {TopNavNotificationIcon} from '@ion/dls/src/icons/TopNavNotificationIcon';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';

/**
 * NotificationButton is a custom React component that displays a notification badge and an icon.
 *
 * @param {Object} props - The component props.
 * @param {number} props.notificationCount - The count of notifications to display in the badge.
 * @param {Function} props.toggleNotificationPanel - The click event handler for the component.
 * @returns {JSX.Element} - The rendered component.
 */
export interface NotificationButtonProps {
  notificationCount: number;
  toggleNotificationPanel?: Function;
}

const useStyles = makeStyles((theme) => ({
  badge : {
    top          : '10px',
    left         : toggleBadgeStyles('8px', '5px', '3px'),
    width        : toggleBadgeStyles('20px', '25px', '31px'),
    height       : '20px',
    borderRadius : toggleBadgeStyles('50%', '10px', '10px'),
    ...dlsAlt('components', 'notificationBadge'),
    fontSize     : '12px'
  },
  notificationIcon : {
    display      : 'flex',
    padding      : '8px',
    borderRadius : '50%',
    '&:hover'    : {
      backgroundColor : dlsColor('paleBlue')
    }
  }
}));

const toggleBadgeStyles = (style1: string, style2: string, style3: string) =>
  ({notificationCount}: NotificationButtonProps) => notificationCount <=9
    ? style1 : notificationCount <= 99 ? style2 : style3
;

const NotificationButton = ({notificationCount, toggleNotificationPanel}: NotificationButtonProps) => {
  const classes = useStyles({notificationCount});

  return (
    <div id={AnalyticsIds.desktopHeaderNotification}
      onClick={toggleNotificationPanel}>
      <Badge badgeContent={notificationCount}
        classes={{
          badge : classes.badge
        }}
        anchorOrigin={{
          vertical   : 'top',
          horizontal : 'right'
        }}
        color='error'
        max={99}
        overlap="circular">
        <div className={classes.notificationIcon}>
          <TopNavNotificationIcon iconColor={dlsColor('grey3')} />
        </div>
      </Badge>
    </div>
  );
};

export default NotificationButton;
